import { ActionIcon, Flex, Group, Modal, SegmentedControl, Stack, Text } from "@mantine/core";
import { IChecklistItemPopulated, IChecklistItemRow, IStampSettings } from "@mctypes/checklist.types";
import { IHistoryLogDepricated, Paths, PathValue } from "@mctypes/global.types";
import React, { useEffect, useState } from "react";
import { ConfirmDialog } from "../../../../components";
import { useAppDispatch, useAppSelector } from "../../../../hooks";

import { IconTrash } from "@tabler/icons-react";
import { ColDef } from "ag-grid-community";
import { ConfirmAbortModal } from "../../../../components/ConfirmAbortModal/ConfirmAbortModal";
import HistoryLogsDepricated from "../../../../components/HistoryLogsDepricated";
import {
  addAppModalThunk,
  setHasUnsavedCommentInRedux,
  setShowSuccessMessageThunk,
} from "../../../../global/globalSlice";
import { AppModalEnums } from "@mcapps/shared/sharedConstants";
import { doGetChecklistRowLogs } from "../checklistAPI";
import {
  deleteChecklistCategoryThunk,
  deleteChecklistItemThunk,
  updateChecklistCategoryThunk,
} from "../checklistSlice";
import ChecklistDetails from "./ChecklistDetails/ChecklistDetails";

const ChecklistModal: React.FC<{
  checklistRow: IChecklistItemRow;
  editable: boolean;
  columns: ColDef[];
  stampSettings: IStampSettings;
  closeIssueItemModal: () => void;
  updateRowAttribute: <P extends Paths<IChecklistItemPopulated, 4>>(
    rowId: string,
    path: P,
    oldValue: string,
    newValue: PathValue<IChecklistItemPopulated, P>,
  ) => void;
}> = ({ checklistRow, editable, columns, closeIssueItemModal, updateRowAttribute, stampSettings }) => {
  const [selectedTab, setSelectedTab] = useState<string>("info");
  const [isItem, setIsRow] = useState<boolean>(false);
  const [confirmModalOpen, setConfirmModalOpen] = React.useState(false);
  const dispatch = useAppDispatch();
  const project = useAppSelector((state) => state.adminReducer.project);
  const checklist = useAppSelector((state) => state.checklistReducer.checklist);
  const hasUnsavedComment = useAppSelector((state) => state.globalReducer.hasUnsavedComment);
  const isMcTemplate = project?.maconomy?.projectNo === 100;

  const removeItem = async () => {
    dispatch(deleteChecklistItemThunk(checklistRow._id));
    closeIssueItemModal();
  };

  const removeCategory = async () => {
    dispatch(deleteChecklistCategoryThunk(checklistRow._id));
    closeIssueItemModal();
  };

  const getLogs = async (projectId: string, itemId: string): Promise<IHistoryLogDepricated[]> => {
    return await doGetChecklistRowLogs(projectId, checklistRow.originalCategory.checklist.toString(), itemId);
  };

  const onAssignedToChange = async <T extends keyof IChecklistItemRow>(
    parameter: string,
    newValue: IChecklistItemRow[T],
    oldValue: string,
  ) => {
    dispatch(updateChecklistCategoryThunk(checklistRow._id, parameter, newValue, oldValue));
    dispatch(setShowSuccessMessageThunk("Sjekklistekategori oppdatert"));
  };
  const onConfirmClose = () => {
    setConfirmModalOpen(false);
    dispatch(setHasUnsavedCommentInRedux(false));
    closeIssueItemModal();
  };

  const closeIssueItemModalWithComments = () => {
    if (hasUnsavedComment === true) {
      setConfirmModalOpen(true);
      return;
    }
    closeIssueItemModal();
  };
  const onOpenIssue = async (props: { itemId: string; boardId: string; app: AppModalEnums }) => {
    const { itemId, boardId, app } = props;

    await dispatch(
      addAppModalThunk({
        itemId,
        boardId,
        projectId: project._id,
        app,
      }),
    );
  };

  useEffect(() => {
    if (checklistRow.originalItem != null) {
      setIsRow(true);
    } else if (checklistRow.originalCategory != null) {
      setIsRow(false);
    }
  }, [checklistRow]);

  return (
    <>
      <ConfirmAbortModal
        isOpen={confirmModalOpen}
        onCancel={() => setConfirmModalOpen(false)}
        onConfirm={() => onConfirmClose()}
        customText="Du har en ulagret kommentar. Er du sikker på at du vil avbryte?"
        customHeader="Ulagret kommentar"
      />
      <Modal
        opened
        onClose={closeIssueItemModalWithComments}
        size="xl"
        withinPortal={true}
        title={
          <Text lineClamp={1} fz="md">
            {isItem === true
              ? `${checklistRow.rowId}: ${checklistRow.title}`
              : `${checklistRow.rowId}: ${checklistRow.originalCategory.title}`}
          </Text>
        }
      >
        <Stack gap="md">
          <Flex align="center" gap="md">
            <SegmentedControl
              color="mc-primary"
              value={selectedTab}
              onChange={setSelectedTab}
              data={[
                { label: "Info", value: "info" },
                { label: "Logg", value: "logs" },
              ]}
              fullWidth
              style={{ maxWidth: "14rem", width: "100%" }}
            />

            <Group justify="flex-end" style={{ flex: 1 }}>
              {editable && isItem === true && (
                <ConfirmDialog confirmCb={removeItem} message={`Vil du virkelig slette rad??`} title={`Slett rad`}>
                  <ActionIcon color="red" variant="light" aria-label="Slett rad">
                    <IconTrash />
                  </ActionIcon>
                </ConfirmDialog>
              )}
              {editable && isItem === false && (
                <ConfirmDialog
                  confirmCb={removeCategory}
                  message={`Vil du virkelig slette kategori og alle rader?`}
                  title={`Fjern kategori`}
                >
                  <ActionIcon color="red" variant="light" aria-label="Fjern kategori">
                    <IconTrash />
                  </ActionIcon>
                </ConfirmDialog>
              )}
            </Group>
          </Flex>

          <Stack gap="md">
            {selectedTab === "info" && (
              <ChecklistDetails
                updateRowAttribute={updateRowAttribute}
                onAssignedToChange={onAssignedToChange}
                checklistRow={checklistRow}
                isItem={isItem}
                isTemplate={checklist.isTemplate}
                editable={editable}
                onOpenIssue={onOpenIssue}
                stampSettings={stampSettings}
              />
            )}
            {selectedTab === "logs" && (
              <HistoryLogsDepricated
                projectId={isMcTemplate === true ? project._id : checklistRow.originalCategory.project.toString()}
                itemId={checklistRow._id}
                modelColumns={columns}
                getLogs={getLogs}
              />
            )}
          </Stack>
        </Stack>
      </Modal>
    </>
  );
};

export default ChecklistModal;
